/* Kasa Subscription Quick-Add bridge
 *
 * Purpose
 * -------
 * On a subscription PDP, the "Añádelos a tu suscripción" cross-sell row
 * uses Horizon's standard quick-add modal. That modal does NOT include
 * Seal's frequency selector, so the form submits without `selling_plan`
 * and Shopify rejects the add.
 *
 * This script:
 *   1. Watches the parent PDP for Seal's `select[name="subs_interval"]`
 *      and remembers the currently-selected selling_plan_id.
 *   2. When a quick-add modal opens from a cross-sell card, injects a
 *      native frequency <select> into the modal so the user can choose
 *      (or it defaults to the parent PDP's current frequency).
 *   3. Intercepts the add-to-cart form submission and injects a hidden
 *      `selling_plan` input with the chosen plan ID.
 *   4. On the first submit attempt Horizon may have already serialised
 *      the form without selling_plan — we also patch FormData via a
 *      fetch interceptor as a safety net.
 *
 * Loading
 * -------
 * Loaded by layout/theme.liquid only on the subscription product templates.
 */

(function () {
  'use strict';

  // ---- Module state -------------------------------------------------------

  /** Currently-selected selling_plan_id on the parent PDP (from Seal's select). */
  let parentPlanId = null;

  /** The Seal-rendered select on the parent PDP, once we find it. */
  let parentSealSelect = null;

  /** Reference to the cross-sell card whose `+` button just opened the modal. */
  let activeCrossSellCard = null;

  /** Whether we've already wired the submit interceptor. */
  let submitInterceptorWired = false;

  // ---- Helpers ------------------------------------------------------------

  function parsePlansFromCard(card) {
    if (!card) return null;
    const raw = card.getAttribute('data-kasa-selling-plans');
    if (!raw) return null;
    try {
      const obj = JSON.parse(raw);
      return (obj && typeof obj === 'object') ? obj : null;
    } catch (_) {
      return null;
    }
  }

  /** Build an ordered array of {label, id} from the plans map on a card. */
  function plansArrayFromCard(card) {
    const plans = parsePlansFromCard(card);
    if (!plans) return [];
    return Object.entries(plans).map(([label, id]) => ({ label, id: String(id) }));
  }

  /**
   * Given the parent's selected plan id, find the matching plan in the
   * cross-sell card's plan list, or fall back to the first plan.
   */
  function resolvePlanIdForCard(card) {
    const plansArr = plansArrayFromCard(card);
    if (!plansArr.length) return null;

    // Try to match by id first (most reliable)
    if (parentPlanId) {
      const match = plansArr.find(p => p.id === String(parentPlanId));
      if (match) return match.id;
    }

    // Fall back to first plan
    return plansArr[0].id;
  }

  // ---- Parent PDP plan tracking -------------------------------------------

  /**
   * Seal renders a <select name="subs_interval"> on the PDP.
   * Each <option> has a data-plan-id attribute with the Shopify selling_plan_id.
   * We track whichever is selected so cross-sell adds inherit the same frequency.
   */
  function readSelectedPlanId(selectEl) {
    if (!selectEl || selectEl.selectedIndex < 0) return null;
    const opt = selectEl.options[selectEl.selectedIndex];
    if (!opt) return null;
    // Seal stores the plan id in data-plan-id on the option
    return opt.dataset.planId || opt.value || null;
  }

  function attachToSealSelect(selectEl) {
    if (!selectEl || selectEl === parentSealSelect) return;
    parentSealSelect = selectEl;
    parentPlanId = readSelectedPlanId(selectEl);

    selectEl.addEventListener('change', function (e) {
      parentPlanId = readSelectedPlanId(e.target);
    });
  }

  function findSealSelect() {
    const candidates = document.querySelectorAll('select[name="subs_interval"]');
    for (const sel of candidates) {
      if (sel.closest('#quick-add-dialog')) continue;
      return sel;
    }
    return null;
  }

  function startWatchingForSealSelect() {
    const existing = findSealSelect();
    if (existing) {
      attachToSealSelect(existing);
      return;
    }

    const observer = new MutationObserver(function () {
      const sel = findSealSelect();
      if (sel) attachToSealSelect(sel);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ---- Frequency selector injection into the modal ------------------------

  /**
   * Inject a native <select> for frequency into the quick-add modal.
   *
   * Visual placement: between the variant-picker and the product-form-component
   * (outside the form, but visually integrated with the variant selectors).
   *
   * Functional placement: the hidden `selling_plan` input is injected
   * INSIDE the form so the submit picks it up. The visible <select> is
   * just UI — it updates the hidden input on change.
   */
  function injectFrequencySelector(modalContent) {
    if (!activeCrossSellCard) return;

    const plansArr = plansArrayFromCard(activeCrossSellCard);
    if (!plansArr.length) return;

    // Don't inject twice
    if (modalContent.querySelector('.kasa-frequency-selector-wrapper')) return;

    const form = modalContent.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    // Determine the default plan to preselect (match parent PDP or first)
    let defaultPlanId = resolvePlanIdForCard(activeCrossSellCard);

    // Build the wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'kasa-frequency-selector-wrapper';
    wrapper.setAttribute('style', [
      // Spacing: more breathing room below so it doesn't crowd
      // the molienda picker / add-to-cart button on desktop.
      'margin-block: 12px 20px',
      'padding: 12px 14px',
      'background: rgb(0 0 0 / 0.03)',
      'border: 1px solid rgb(0 0 0 / 0.12)',
      'border-radius: 20px',
      'font-size: 16px',
      // Width: full width of the parent. In mobile the modal's
      // grid would otherwise restrict the wrapper to 1 column;
      // grid-column: 1 / -1 makes it span all columns. In desktop
      // the parent (.product-details) is flex-column so width:100%
      // does the same job. Setting both is safe in either layout.
      'width: 100%',
      'grid-column: 1 / -1',
      'box-sizing: border-box',
    ].join(';'));

    const label = document.createElement('label');
    label.setAttribute('for', 'kasa-freq-select');
    label.setAttribute('style', 'display:block; margin-bottom:8px; font-weight:500; font-size:16px; color: var(--color-foreground);');
    label.textContent = 'Frecuencia';

    // Solid down-triangle for the custom dropdown arrow, matching the
    // "EN GRANO" select arrow style. Encoded as data URI so it works
    // without a Liquid asset lookup. Color matches the theme foreground.
    const caretSvg = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='7' viewBox='0 0 10 7'><path d='M0 0L10 0L5 7Z' fill='%2341261C'/></svg>";

    const select = document.createElement('select');
    select.id = 'kasa-freq-select';
    select.name = 'kasa_freq_display'; // not `selling_plan` — we mirror to hidden
    select.setAttribute('style', [
      'width:100%',
      // Padding per Carles: 11 sides, 12 top/bottom. Right padding
      // is larger to leave room for the triangle arrow.
      'padding:12px 32px 12px 11px',
      'border:1px solid rgb(0 0 0 / 0.2)',
      'border-radius:14px',
      'background-color:var(--color-background)',
      'background-image:url("' + caretSvg + '")',
      'background-repeat:no-repeat',
      'background-position:right 12px center',
      'background-size:10px',
      'color:var(--color-foreground)',
      'font-size:16px',
      'cursor:pointer',
      'font-family:inherit',
      // Kill the native dropdown arrow so only our custom one shows.
      'appearance:none',
      '-webkit-appearance:none',
      '-moz-appearance:none',
      'outline:none',
      'box-sizing:border-box',
    ].join(';'));

    plansArr.forEach(function (plan) {
      const opt = document.createElement('option');
      opt.value = plan.id;
      opt.textContent = plan.label;
      if (plan.id === defaultPlanId) opt.selected = true;
      select.appendChild(opt);
    });

    wrapper.appendChild(label);
    wrapper.appendChild(select);

    // Ensure the hidden selling_plan input exists in the form
    ensureSellingPlanInput(form, defaultPlanId);

    // Keep the hidden input in sync when user changes the visible select
    select.addEventListener('change', function () {
      ensureSellingPlanInput(form, select.value);
    });

    // ---- Placement: between variant-picker and product-form-component ----
    // We want the selector to live visually right after the variant
    // selectors but before the add-to-cart button. The form contains only
    // the buy button (variant-picker is its sibling in product-details),
    // so we insert the wrapper just before product-form-component, which
    // puts it between the variant picker and the form.
    const productFormComponent = modalContent.querySelector('product-form-component');
    const variantPicker = modalContent.querySelector('variant-picker');

    if (productFormComponent && productFormComponent.parentNode) {
      productFormComponent.parentNode.insertBefore(wrapper, productFormComponent);
    } else if (variantPicker && variantPicker.parentNode && variantPicker.nextSibling) {
      variantPicker.parentNode.insertBefore(wrapper, variantPicker.nextSibling);
    } else {
      // Fallback: insert at top of form
      form.insertBefore(wrapper, form.firstChild);
    }
  }

  /** Ensure a hidden selling_plan input exists in `form` with the given planId. */
  function ensureSellingPlanInput(form, planId) {
    if (!form || !planId) return;
    let input = form.querySelector('input[name="selling_plan"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      form.appendChild(input);
    }
    input.value = String(planId);
  }

  /** Watch modal content for DOM changes and (re-)inject the selector when needed. */
  function watchModalContentForInjection() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const observer = new MutationObserver(function () {
      if (!activeCrossSellCard) return;
      injectFrequencySelector(modalContent);
    });

    observer.observe(modalContent, { childList: true, subtree: true });
  }

  // ---- Cross-sell card / modal wiring ------------------------------------

  function trackCrossSellOpens() {
    document.addEventListener(
      'pointerdown',
      function (e) {
        const card = e.target && e.target.closest && e.target.closest('[data-kasa-subscription-cross-sell="true"]');
        if (card) {
          activeCrossSellCard = card;
        }
      },
      true
    );

    document.addEventListener('dialog:close', function () {
      activeCrossSellCard = null;
    });
  }

  // ---- Submit interceptor (safety net) ------------------------------------

  /**
   * Capture-phase submit interceptor. Ensures selling_plan is set on the
   * form right before Horizon serialises it. This covers the first-submit
   * case where the hidden input may not have been present yet when the
   * component first read the form.
   */
  function wireSubmitInterceptor() {
    if (submitInterceptorWired) return;
    submitInterceptorWired = true;

    document.addEventListener(
      'submit',
      function (e) {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;

        const inDialog = form.closest('#quick-add-dialog');
        if (!inDialog) return;

        const action = form.getAttribute('action') || '';
        if (!action.includes('/cart/add')) return;

        if (!activeCrossSellCard) return;

        // Read the current value of the kasa frequency select if present
        const freqSelect = form.querySelector('#kasa-freq-select');
        const planId = freqSelect ? freqSelect.value : resolvePlanIdForCard(activeCrossSellCard);

        if (!planId) return;
        ensureSellingPlanInput(form, planId);
      },
      true
    );

    // Patch fetch so that even if Horizon re-reads FormData after the
    // submit event, our selling_plan is included. We do this by patching
    // FormData to always include selling_plan when the form is inside the
    // quick-add dialog and we have an active cross-sell card.
    patchFetchFormData();
  }

  /**
   * Patch fetch to intercept /cart/add requests from the quick-add dialog
   * and ensure selling_plan is present in the request body.
   *
   * This handles the race condition where Horizon's ProductFormComponent
   * calls fetch() with FormData that was captured before our submit handler
   * could inject the hidden input.
   */
  function patchFetchFormData() {
    if (window.__kasaFetchPatched) return;
    window.__kasaFetchPatched = true;

    const origFetch = window.fetch;
    window.fetch = async function (input, init) {
      try {
        // Only act on /cart/add POST requests from the quick-add dialog
        const url = typeof input === 'string' ? input : (input instanceof Request ? input.url : '');
        const method = (init && init.method) ? init.method.toUpperCase() : 'GET';

        if (
          method === 'POST' &&
          url.includes('/cart/add') &&
          activeCrossSellCard &&
          init && init.body instanceof FormData
        ) {
          const fd = init.body;
          // Only patch if selling_plan is missing or empty
          if (!fd.get('selling_plan')) {
            const freqSelect = document.querySelector('#quick-add-dialog #kasa-freq-select');
            const planId = freqSelect
              ? freqSelect.value
              : resolvePlanIdForCard(activeCrossSellCard);
            if (planId) {
              fd.set('selling_plan', String(planId));
            }
          }
        }
      } catch (_) {
        // Never break the actual request
      }
      return origFetch.apply(this, arguments);
    };
  }

  // ---- Bootstrap ---------------------------------------------------------

  function init() {
    if (!document.body.hasAttribute('data-kasa-subscription-pdp')) return;

    startWatchingForSealSelect();
    trackCrossSellOpens();
    wireSubmitInterceptor();
    watchModalContentForInjection();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
